import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
} from 'react';
import {
	fetchInstanceBranding,
	instanceLogoUrl,
} from '../services/instanceApi';
import { InstanceBranding } from '../types/types';

type InstanceStatus = 'loading' | 'ready' | 'error';

interface InstanceContextValue {
	branding: InstanceBranding | null;
	logoSrc: string;
	status: InstanceStatus;
	reload: () => Promise<void>;
}

const InstanceContext = createContext<InstanceContextValue>({
	branding: null,
	logoSrc: '',
	status: 'loading',
	reload: () => Promise.resolve(),
});

export const InstanceProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const [branding, setBranding] = useState<InstanceBranding | null>(null);
	const [status, setStatus] = useState<InstanceStatus>('loading');

	const reload = useCallback(async () => {
		const next = await fetchInstanceBranding();
		if (next === null) {
			setBranding(null);
			setStatus('error');
			return;
		}
		setBranding(next);
		setStatus('ready');
	}, []);

	useEffect(() => {
		void reload();
	}, [reload]);

	return (
		<InstanceContext.Provider
			value={{
				branding,
				logoSrc: status === 'loading' ? '' : instanceLogoUrl(branding),
				status,
				reload,
			}}
		>
			{children}
		</InstanceContext.Provider>
	);
};

export function useInstance(): InstanceContextValue {
	return useContext(InstanceContext);
}
